import {
  calculateMonthlyAmount,
  calculateProration,
  formatAmount,
  type TenantTier,
  TIER_CONFIG,
} from "@repo/shared";
import { Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { SubscriptionDetails } from "@/lib/api/billing";
import { useChangeSubscription, useCreateCheckout } from "@/lib/hooks/use-billing";

type CheckoutDialogProps = {
  open: boolean;
  onClose: () => void;
  targetTier: TenantTier;
  currentSubscription: SubscriptionDetails | null;
  t: {
    subscribeTo: string;
    changeTo: string;
    seats: string;
    removeSeat: string;
    addSeat: string;
    minSeats: string;
    monthlyTotal: string;
    dueToday: string;
    startingNextMonth: string;
    cancel: string;
    proceedToPayment: string;
    confirmChange: string;
    processing: string;
  };
};

export function CheckoutDialog({
  open,
  onClose,
  targetTier,
  currentSubscription,
  t,
}: CheckoutDialogProps) {
  const tierConfig = TIER_CONFIG[targetTier];
  const [seats, setSeats] = useState(tierConfig.minSeats);
  const [error, setError] = useState<string | null>(null);

  const checkoutMutation = useCreateCheckout();
  const changeMutation = useChangeSubscription();
  const isPending = checkoutMutation.isPending || changeMutation.isPending;

  // Reset seats when dialog opens - use current seats if same tier, otherwise minimum
  useEffect(() => {
    if (open) {
      const isSameTier = currentSubscription?.tier === targetTier;
      const currentSeats = currentSubscription?.seats ?? tierConfig.minSeats;
      // Clamp to the tier floor — a stale DB row can hold fewer seats than the
      // tier minimum, and starting below it would wedge the +/- stepper.
      setSeats(isSameTier ? Math.max(currentSeats, tierConfig.minSeats) : tierConfig.minSeats);
      setError(null);
    }
  }, [
    open,
    targetTier,
    currentSubscription?.tier,
    currentSubscription?.seats,
    tierConfig.minSeats,
  ]);

  // Treat as new subscription if no subscription, status is "none"/"pending", or no Mollie subscription ID yet
  const isNewSubscription =
    !currentSubscription ||
    currentSubscription.status === "none" ||
    currentSubscription.status === "pending" ||
    !currentSubscription.mollieSubscriptionId;
  const monthlyAmount = calculateMonthlyAmount(targetTier, seats);

  // Calculate proration for plan changes
  const proration = (() => {
    if (
      isNewSubscription ||
      !currentSubscription?.currentPeriodEnd ||
      !currentSubscription?.currentPeriodStart
    ) {
      return null;
    }

    const now = new Date();
    const periodEnd = new Date(currentSubscription.currentPeriodEnd);
    const periodStart = new Date(currentSubscription.currentPeriodStart);

    const totalDays = Math.ceil(
      (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
    );
    const daysRemaining = Math.max(
      0,
      Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
    );

    return calculateProration(
      currentSubscription.tier as TenantTier,
      currentSubscription.seats,
      targetTier,
      seats,
      daysRemaining,
      totalDays,
    );
  })();

  const handleSeatChange = (delta: number) => {
    setSeats(Math.max(seats + delta, tierConfig.minSeats));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      if (isNewSubscription) {
        const result = await checkoutMutation.mutateAsync({ tier: targetTier, seats });
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        }
      } else {
        await changeMutation.mutateAsync({ tier: targetTier, seats });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to process request");
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isNewSubscription ? t.subscribeTo : t.changeTo}{" "}
            {targetTier.charAt(0).toUpperCase() + targetTier.slice(1)}
          </DialogTitle>
          <DialogDescription>
            {t.minSeats}: {tierConfig.minSeats}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {/* Seat selector */}
          <div className="space-y-2">
            <span className="text-sm font-medium">{t.seats}</span>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                tooltip={t.removeSeat}
                onClick={() => handleSeatChange(-1)}
                disabled={seats <= tierConfig.minSeats}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-2xl font-bold">{seats}</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                tooltip={t.addSeat}
                onClick={() => handleSeatChange(1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="border-t pt-4">
            {/* Pricing summary */}
            <div className="space-y-2">
              {isNewSubscription ? (
                <div className="flex justify-between">
                  <span>{t.monthlyTotal}</span>
                  <span className="font-bold">{formatAmount(monthlyAmount)}/mo</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>{t.dueToday}</span>
                    <span
                      className={
                        proration && proration.immediate > 0 ? "font-bold" : "text-muted-foreground"
                      }
                    >
                      {proration ? formatAmount(proration.immediate) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.startingNextMonth}</span>
                    <span className="font-bold">{formatAmount(monthlyAmount)}/mo</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          <Button type="button" onClick={handleSubmit} loading={isPending}>
            {isNewSubscription ? t.proceedToPayment : t.confirmChange}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
